import { ONE_MINUTE_MILLIS, Stream, publish, requiresLogout } from "@shared";
import { snapshot } from "./snapshot.svelte";
import { messagesRead, storageStore } from "./state";
import { userStore } from "./state/users/state";
import { background } from "./stores/background";
import { connectionUnusableChanged } from "./stores/network";
import { withPausedStores } from "./utils/stores";
// The production index.html starts the worker from an inline script (`generateStartupScript` in
// rollup.extras.mjs), so that it is downloaded, compiled and running by the time this code has
// loaded. Messages posted before it is ready are queued. That index.html and this code always
// come from the same build, so it is the worker this version would have started. The dev server
// and the tests don't prestart one.
function takePrestartedWorker() {
    const prestarted = window.OC_PRESTARTED_WORKER;
    window.OC_PRESTARTED_WORKER = undefined;
    return prestarted;
}
export class WorkerAgent {
    #worker;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    #inflightRequests = new Map();
    #logger;
    #sessionExpired = false;
    nextCorrelationId = 0;
    // `onSyncHead` is passed in rather than published on the global pubsub because the only
    // listener is the OpenChat instance that owns this agent: routing it globally would keep a
    // discarded instance alive for the lifetime of the page.
    constructor(config, onSyncHead = () => { }) {
        console.debug("WORKER_CLIENT: loading worker with version: ", config.websiteVersion);
        const workerUrl = `/worker.js?v=${config.websiteVersion}`;
        this.#worker =
            takePrestartedWorker() ??
                new Worker(new URL(workerUrl, import.meta.url), {
                    type: "module",
                });
        this.#logger = config.logger;
        this.#worker.onmessage = (ev) => {
            if (!ev.data) {
                console.debug("WORKER_CLIENT: event message with no data received");
                return;
            }
            const data = ev.data;
            if (data.kind === "worker_event") {
                if (data.event.subkind === "messages_read_from_server") {
                    const { chatId, readByMeUpTo, threadsRead, dateReadPinned } = data.event;
                    withPausedStores(() => {
                        messagesRead.syncWithServer(chatId, readByMeUpTo, threadsRead, dateReadPinned);
                    });
                }
                if (data.event.subkind === "storage_updated") {
                    storageStore.set(data.event.status);
                }
                if (data.event.subkind === "users_loaded") {
                    userStore.addMany(data.event.users);
                }
                if (data.event.subkind === "connection_unusable") {
                    connectionUnusableChanged(data.event.unusable);
                }
                if (data.event.subkind === "sync_head") {
                    onSyncHead({ userId: data.event.userId, version: data.event.version });
                }
            }
            else if (data.kind === "worker_response") {
                console.debug("WORKER_CLIENT: response: ", ev);
                this.#resolveResponse(data);
            }
            else if (data.kind === "worker_error") {
                console.debug("WORKER_CLIENT: error: ", ev);
                this.#resolveError(data);
            }
            else {
                console.debug("WORKER_CLIENT: unknown message: ", ev);
            }
        };
        const initArgs = {
            kind: "init",
            icUrl: config.icUrl ?? window.location.origin,
            iiDerivationOrigin: config.iiDerivationOrigin,
            openStorageIndexCanister: config.openStorageIndexCanister,
            groupIndexCanister: config.groupIndexCanister,
            notificationsCanister: config.notificationsCanister,
            identityCanister: config.identityCanister,
            onlineCanister: config.onlineCanister,
            dailyPuzzleCanister: config.dailyPuzzleCanister,
            userIndexCanister: config.userIndexCanister,
            translationsCanister: config.translationsCanister,
            registryCanister: config.registryCanister,
            internetIdentityUrl: config.internetIdentityUrl,
            nfidUrl: config.nfidUrl,
            userGeekApiKey: config.userGeekApiKey,
            enableMultiCrypto: config.enableMultiCrypto,
            blobUrlPattern: config.blobUrlPattern,
            priceServiceUrl: config.priceServiceUrl,
            canisterUrlPath: config.canisterUrlPath,
            proposalBotCanister: config.proposalBotCanister,
            signInWithEmailCanister: config.signInWithEmailCanister,
            signInWithEthereumCanister: config.signInWithEthereumCanister,
            signInWithSolanaCanister: config.signInWithSolanaCanister,
            oneSecForwarderCanister: config.oneSecForwarderCanister,
            oneSecMinterCanister: config.oneSecMinterCanister,
            websiteVersion: config.websiteVersion,
            rollbarApiKey: config.rollbarApiKey,
            env: config.env,
            bitcoinMainnetEnabled: config.bitcoinMainnetEnabled,
            groupInvite: config.groupInvite,
            accountLinkingCodesEnabled: config.accountLinkingCodesEnabled,
        };
        this.send(initArgs);
        // The worker judges the connection from its queries, and needs to know when the app is
        // hidden, when their failures say nothing about the connection. Lives as long as the page.
        background.subscribe((hidden) => {
            this.send({ kind: "setAppHidden", hidden }).catch((err) => console.warn("Unable to tell the worker whether the app is hidden", err));
        });
        window.setInterval(() => this.#monitorPendingRequests(), ONE_MINUTE_MILLIS);
    }
    send(request) {
        return new Promise(this.#sendRequestInternal(request));
    }
    stream(request) {
        return new Stream(this.#sendRequestInternal(request));
    }
    responseHandler(correlationId) {
        return (resolve, reject) => {
            this.#inflightRequests.set(correlationId, {
                resolve,
                reject,
            });
        };
    }
    #sendRequestInternal(req) {
        const correlationId = this.nextCorrelationId++;
        try {
            this.#worker.postMessage({
                ...snapshot(req),
                correlationId,
            });
        }
        catch (err) {
            console.error("Error sending postMessage to worker", err);
            throw err;
        }
        return this.responseHandler(correlationId);
    }
    // Reported once per crossing of the limit: a wedged worker sits above it for as long as the
    // tab stays open, and a report every poll would be one item per minute saying the same thing
    #pendingLimitReported = false;
    #monitorPendingRequests() {
        const pendingRequests = this.#inflightRequests.size;
        if (pendingRequests >= 100) {
            if (!this.#pendingLimitReported) {
                this.#pendingLimitReported = true;
                this.#logger.error("Pending request count exceeded limit", {
                    count: pendingRequests,
                });
            }
        }
        else {
            this.#pendingLimitReported = false;
        }
    }
    #resolveResponse(data) {
        const promise = this.#inflightRequests.get(data.correlationId);
        if (promise !== undefined) {
            promise.resolve(data.response, data.final);
            if (data.final) {
                this.#inflightRequests.delete(data.correlationId);
            }
        }
        else {
            this.#logUnexpected(data.requestKind, data.correlationId);
        }
    }
    #resolveError(data) {
        const error = JSON.parse(data.error);
        // A request rejected because the session is gone only logged the user out if nobody
        // caught it and it reached the window's unhandledrejection handler. Background pollers
        // catch their own failures, so an expired delegation left the client polling on a timer
        // for as long as the tab stayed open - thousands of identical failures from one client.
        // Only once: a burst of pollers all fail together when a delegation expires, and logout
        // ends in a page navigation.
        if (!this.#sessionExpired && requiresLogout(error)) {
            this.#sessionExpired = true;
            publish("sessionExpired");
        }
        const promise = this.#inflightRequests.get(data.correlationId);
        if (promise !== undefined) {
            promise.reject(error);
            this.#inflightRequests.delete(data.correlationId);
        }
        else {
            this.#logUnexpected(data.requestKind, data.correlationId);
        }
    }
    #logUnexpected(kind, correlationId) {
        console.error(`WORKER_CLIENT: unexpected correlationId received (${correlationId})`, kind);
    }
}
